import { ReactComponent as IconPrevious } from '@/assets/svgs/previous-arrow.svg'
import { ReactComponent as IconChevron } from '@/assets/svgs/profile-chevron.svg'
import Button from '@/components/Button'
import Portal from '@/components/Portal'
import { ToastContext } from '@/context/toast/index'
import useEclips from '@/hooks/use-eclips/index'
import usePage from '@/hooks/use-page'
import useTemplates from '@/hooks/use-templates/index'
import { NavLink, useNavigate, useQuery } from '@/lib/router-dom/index'
import { useFormik } from 'formik'
import { useContext, useEffect } from 'react'
import ReactDatePicker from 'react-datepicker'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import Breadcrumbs from '@/componentsDS/Breadcrumbs/index'

const validationSchema = yup.object({
	name: yup
		.string('Enter your Eclip Name')
		.required('Name is required')
		.nullable(),
	templateId: yup
		.string('Select your Template')
		.required('Template is required')
		.nullable(),
	expectedDateOfCompletion: yup
		.string('Enter your Expected Date of Completion')
		.required('Expected Date of Completion is required')
		.nullable(),
	referenceNumber: yup
		.string('Enter your Reference Number')
		.min(8, 'Reference Number should be minimum of 8 characters length')
		.max(32, 'Reference Number should be of 32 characters length')
		.required('Reference Number is required')
		.nullable(),
})

const Page = () => {
	/*=================================================
		General
	/================================================*/

	const { addToastMessage } = useContext(ToastContext)
	const { goTo } = useNavigate();
	const query = useQuery()
	const { t } = useTranslation()
	const formik = useFormik({
		initialValues: {
			id: 'test',
			name: '',
			templateId: '',
			expectedDateOfCompletion: new Date(),
			referenceNumber: '',
		},
		validationSchema: validationSchema,
		onSubmit: async (data) => {
			const response = await create(data)
			goTo(`/eclip/send/${response.id}`)
			addToastMessage('Eclip successfully created', 'success')
		},
	})

	const { create } = useEclips()
	const { fetch, items: templates } = useTemplates()
	const pageTitle = t('createEclipTitle')

	const handleCancel = () => {
		goTo('/eclip')
	}

	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{
				label: '',
				link: `/`,
			},
			{
				label: 'Package Sharing',
				link: `/eclip`
			},
			{
				label: 'Create Package',
				link: `/eclip/create`
			},
		],
	})

	const ErrorMessage = ({ name }) =>
		formik.errors[name] && formik.touched[name] ? (
		<div className="error-message">{formik.errors[name]}</div>
		) : null

	useEffect(() => {
		fetch()
	}, [])

	useEffect(() => {
		const selectedTemplateId = query.get('templateId')
		formik.setFieldValue('templateId', selectedTemplateId)
	}, [query])

	return (
		<div className="eclip-create__content">
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">{pageTitle}</div>
				</div>

				<div className="title-header__right"></div>
			</div>

			<form className="eclip-create__form" onSubmit={formik.handleSubmit}>
				<div className="eclip-create__form-row">
					<div className="eclip-create__input-label">{t('eclipName')}</div>
					<input
						className="eclip-create__input-field"
						name="name"
						placeholder={t('eclipNamePlaceholder')}
						value={formik.values.name}
						onChange={formik.handleChange}
					/>
					<ErrorMessage name="name" />
				</div>
				<div className="eclip-create__form-row">
					<div className="eclip-create__input-label">{t('eclipReference')}</div>
					<input
						className="eclip-create__input-field"
						name="referenceNumber"
						placeholder={t('eclipReferencePlaceholder')}
						value={formik.values.referenceNumber}
						onChange={formik.handleChange}
					/>
					<div className="eclip-create__input-description">
						{t('eclipReferenceDescription')}
					</div>
					<ErrorMessage name="referenceNumber" />
				</div>
				<div className="eclip-create__form-row">
					<div className="eclip-create__input-label">{t('eclipTemplate')}</div>
					<div className="select">
						<div className="select__icon">
							<IconChevron />
						</div>
						<div className="select__options">
						<select
							name="templateId"
							value={formik.values.templateId}
							onChange={formik.handleChange}
						>
							<option value="" disabled>
								{t('select')}
							</option>
							{templates.map((template, index) => (
								<option key={index} value={template.id}>
									{template.name}
								</option>
							))}
						</select>
						</div>
					</div>
					<ErrorMessage name="templateId" />
				</div>
				<div className="eclip-create__form-row__low-width">
				<div className="eclip-create__input-label">
					{t('eclipExpectedDate')}
				</div>
				<div className="date_picker__container">
					<ReactDatePicker
						dateFormat="d MMM yyyy"
						minDate={new Date()}
						value={formik.values.expectedDateOfCompletion}
						selected={
							(formik.values.expectedDateOfCompletion &&
							new Date(formik.values.expectedDateOfCompletion)) ||
							null
						}
						showTimeSelect={false}
						todayButton="Today"
						dropdownMode="select"
						isClearable
						shouldCloseOnSelect
						showMonthDropdown
						showYearDropdown
						placeholderText={t('eclipExpectedDatePlaceholder')}
						onChange={(val) => {
							formik.setFieldValue('expectedDateOfCompletion', val)
						}}
					/>
					<ErrorMessage name="expectedDateOfCompletion" />
				</div>
				</div>
				<div className="DS__layout__footer">
					<div className="layout-default__sticky-actions">
					<div className="layout-default__sticky-actions__left">
						<Button
							text={t('Cancel')}
							sizing="Medium"
							full
							noborder
							maxWidth="154"
							submit={handleCancel}
						/>
					</div>
					<div
						className="layout-default__sticky-actions__right"
						style={{ display: 'flex' }}
					>
						<div className="overview__button_wrap">
						<Button
							text={t('submit')}
							sizing="Medium"
							full
							maxWidth="154"
							type="submit"
						/>
						</div>
					</div>
					</div>
				</div>
			</form>
		</div>
	)
}

export default Page
